import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Trophy, Lock } from 'lucide-react'

interface Achievement {
  id: number
  code: string
  name: string
  description: string
  category: string
  tier: string
  xp_reward: number
  earned: boolean
  earned_at?: string
}

interface AchievementsData {
  achievements: Achievement[]
  by_category: Record<string, Achievement[]>
  total: number
  earned: number
}

const categoryInfo: Record<string, { icon: string; color: string; label: string }> = {
  growth: { icon: '🌱', color: 'green', label: 'Growth' },
  consistency: { icon: '💪', color: 'blue', label: 'Consistency' },
  skill: { icon: '🧠', color: 'purple', label: 'Skill' },
  challenge: { icon: '⚔️', color: 'yellow', label: 'Challenge' },
}

const tierStyles: Record<string, string> = {
  bronze: 'border-tier-bronze bg-tier-bronze/10',
  silver: 'border-tier-silver bg-tier-silver/10',
  gold: 'border-tier-gold bg-tier-gold/10',
}

export default function StudentAchievements() {
  const { data, isLoading } = useQuery<AchievementsData>({
    queryKey: ['student-achievements'],
    queryFn: async () => {
      const response = await api.get('/api/student/achievements')
      return response.data
    },
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-40 bg-surface-card rounded-xl"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Trophy className="w-8 h-8 text-quest-accent" />
          Achievements
        </h1>
        <p className="text-gray-400 mt-1">
          {data?.earned || 0} of {data?.total || 0} achievements unlocked
        </p>
      </div>

      {/* Progress bar */}
      <div className="card">
        <div className="flex justify-between mb-2">
          <span className="text-sm text-gray-400">Collection Progress</span>
          <span className="text-sm font-medium">
            {data ? Math.round((data.earned / data.total) * 100) : 0}%
          </span>
        </div>
        <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-quest-accent to-yellow-500"
            style={{
              width: `${data ? (data.earned / data.total) * 100 : 0}%`,
            }}
          />
        </div>
      </div>

      {/* Achievements by category */}
      {Object.entries(data?.by_category || {}).map(([category, achievements]) => {
        const info = categoryInfo[category] || { icon: '🏆', color: 'gray', label: category }

        return (
          <section key={category}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <span className="text-2xl">{info.icon}</span>
              {info.label}
              <span className="text-sm font-normal text-gray-400 ml-2">
                {achievements.filter((a) => a.earned).length}/{achievements.length}
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {achievements.map((achievement) => (
                <AchievementCard key={achievement.id} achievement={achievement} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function AchievementCard({ achievement }: { achievement: Achievement }) {
  const { earned, tier, name, description, xp_reward } = achievement

  return (
    <div
      className={`achievement-card ${earned ? 'earned' : 'locked'} ${tierStyles[tier] || ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`text-3xl ${earned ? '' : 'grayscale opacity-50'}`}>
          {categoryInfo[achievement.category]?.icon || '🏆'}
        </div>
        {!earned && <Lock className="w-4 h-4 text-gray-500" />}
      </div>

      <h3 className={`font-medium mb-1 ${earned ? '' : 'text-gray-500'}`}>
        {name}
      </h3>
      <p className={`text-sm ${earned ? 'text-gray-400' : 'text-gray-600'}`}>
        {description}
      </p>

      <div className="mt-3 flex items-center justify-between">
        <span className={`text-xs font-medium uppercase ${
          tier === 'gold' ? 'text-tier-gold' :
          tier === 'silver' ? 'text-tier-silver' :
          'text-tier-bronze'
        }`}>
          {tier}
        </span>
        <span className={`text-sm ${earned ? 'text-quest-accent' : 'text-gray-500'}`}>
          +{xp_reward} XP
        </span>
      </div>
    </div>
  )
}
